import { RoutePath } from 'routes/route-path.constant';
import { LocalStorageKeys, MultipleLocalStorageKeys } from 'constants/local-storage';
import { handleRedirectToPwa } from 'helpers/redirect-to-pwa';

export type PageType = {
    customWrapper?: boolean,
    scrollableContent?: boolean,
    header: {
        email: boolean,
        logo: boolean,
        progress: {
            percents: number | null,
            label: string | null
        } | null
    },
    route: {
        next: (data?: any) => string,
    },
    events: {
        onEnter?: (data?: any) => string,
        onLeave?: (data?: any) => string,
    },
    // @description: string - required & string[] - any of the item required
    requiredKeys?: (string | string[])[],
    footerAtTheBottom?: boolean,
};

export type PageConfigurationType = {
    [key: string]: PageType;
};

export const DEFAULT_PAGES_CONFIGURATION: PageConfigurationType = {
    [RoutePath.BodyGender]: {
        header: {
            email: false,
            logo: true,
            progress: {
                percents: 8,
                label: '',
            },
        },
        route: {
            next: () => RoutePath.LoseWeight,
        },
        events: {
            onLeave: () => 'SemaRxWeightGenderClicked'
        }
    },
    [RoutePath.LoseWeight]: {
        header: {
            email: false,
            logo: true,
            progress: {
                percents: 16,
                label: '',
            },
        },
        route: {
            next: () => RoutePath.FamilyWeight,
        },
        events: {
            onLeave: () => 'SemaRxWeightLoseWeightClicked'
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender]
        ]
    },
    [RoutePath.FamilyWeight]: {
        header: {
            email: false,
            logo: true,
            progress: {
                percents: 24,
                label: '',
            },
        },
        route: {
            next: () => RoutePath.FamilyWeightTip,
        },
        events: {
            onLeave: () => 'SemaRxWeightFamilyWeightClicked'
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender]
        ]
    },
    [RoutePath.FamilyWeightTip]: {
        customWrapper: true,
        header: {
            email: false,
            logo: true,
            progress: {
                percents: null,
                label: '',
            },
        },
        route: {
            next: () => RoutePath.PastAttempts,
        },
        events: {
            onLeave: () => 'SemaRxWeightFamilyWeightTipClicked'
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender]
        ]
    },
    [RoutePath.PastAttempts]: {
        header: {
            email: false,
            logo: true,
            progress: {
                percents: 32,
                label: '',
            },
        },
        route: {
            next: () => RoutePath.WeightLoss,
        },
        events: {
            onLeave: () => 'SemaRxWeightPastAttemptsClicked'
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender]
        ]
    },
    [RoutePath.WeightLoss]: {
        header: {
            email: false,
            logo: true,
            progress: {
                percents: null,
                label: '',
            },
        },
        route: {
            next: () => RoutePath.LoseWeightReason,
        },
        events: {
            onLeave: () => 'SemaRxWeightWeightLossClicked'
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender]
        ]
    },
    [RoutePath.LoseWeightReason]: {
        header: {
            email: false,
            logo: true,
            progress: {
                percents: 40,
                label: '',
            },
        },
        route: {
            next: () => RoutePath.WeightFactors,
        },
        events: {
            onLeave: () => 'SemaRxWeightLoseWeightReasonClicked'
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender]
        ]
    },
    [RoutePath.WeightFactors]: {
        header: {
            email: false,
            logo: true,
            progress: {
                percents: null,
                label: '',
            },
        },
        route: {
            next: () => RoutePath.PrescriptionMedications,
        },
        events: {
            onLeave: () => 'SemaRxWeightWeightFactorsClicked'
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender],
            LocalStorageKeys[RoutePath.LoseWeightReason]
        ]
    },
    [RoutePath.PrescriptionMedications]: {
        header: {
            email: false,
            logo: true,
            progress: {
                percents: 48,
                label: '',
            },
        },
        route: {
            next: () => RoutePath.PrescriptionMedicationsTip,
        },
        events: {
            onLeave: () => 'SemaRxWeightPrescriptionMedicationsClicked'
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender],
            LocalStorageKeys[RoutePath.LoseWeightReason]
        ]
    },
    [RoutePath.PrescriptionMedicationsTip]: {
        customWrapper: true,
        header: {
            email: false,
            logo: true,
            progress: {
                percents: null,
                label: '',
            },
        },
        route: {
            next: () => RoutePath.BodyHeight,
        },
        events: {
            onLeave: () => 'SemaRxWeightPrescriptionMedicationsTipClicked'
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender],
            LocalStorageKeys[RoutePath.LoseWeightReason]
        ]
    },
    [RoutePath.BodyHeight]: {
        header: {
            email: false,
            logo: true,
            progress: {
                percents: 52,
                label: '',
            },
        },
        route: {
            next: () => RoutePath.BodyWeight,
        },
        events: {
            onLeave: () => 'SemaRxWeightBodyHeightClicked'
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender],
            LocalStorageKeys[RoutePath.LoseWeightReason]
        ]
    },
    [RoutePath.BodyWeight]: {
        header: {
            email: false,
            logo: true,
            progress: {
                percents: 56,
                label: '',
            },
        },
        route: {
            next: () => RoutePath.WeightMetabolicRate,
        },
        events: {
            onLeave: () => 'SemaRxWeightBodyWeightClicked'
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender],
            LocalStorageKeys[RoutePath.LoseWeightReason],
            MultipleLocalStorageKeys.bodyHeightWeightKeys.unitSystem,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.heightFt,
        ]
    },
    [RoutePath.WeightMetabolicRate]: {
        header: {
            email: false,
            logo: true,
            progress: {
                percents: null,
                label: '',
            },
        },
        route: {
            next: () => RoutePath.BodyIdealWeight,
        },
        events: {
            onEnter: () => 'SemaRxWeightMetabolicRateViewed',
            onLeave: () => 'SemaRxWeightMetabolicRateClicked'
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender],
            LocalStorageKeys[RoutePath.LoseWeightReason],
            MultipleLocalStorageKeys.bodyHeightWeightKeys.unitSystem,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.heightFt,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.weightLbs
        ]
    },
    [RoutePath.BodyIdealWeight]: {
        header: {
            email: false,
            logo: true,
            progress: {
                percents: 60,
                label: '',
            },
        },
        route: {
            next: (data) => data.eligibleBMIValue ? RoutePath.BeforeAfterMedication : RoutePath.DeclineUser,
        },
        events: {
            onLeave: () => 'SemaRxWeightBodyIdealWeightClicked'
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender],
            LocalStorageKeys[RoutePath.LoseWeightReason],
            MultipleLocalStorageKeys.bodyHeightWeightKeys.unitSystem,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.heightFt,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.weightLbs
        ]
    },
    [RoutePath.BeforeAfterMedication]: {
        scrollableContent: true,
        header: {
            email: false,
            logo: true,
            progress: {
                percents: null,
                label: '',
            },
        },
        route: {
            next: () => RoutePath.HealthCondition,
        },
        events: {
            onEnter: () => 'SemaRxWeightBeforeAfterViewed',
            onLeave: () => 'SemaRxWeightBeforeAfterClicked'
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender],
            LocalStorageKeys[RoutePath.LoseWeightReason],
            MultipleLocalStorageKeys.bodyHeightWeightKeys.unitSystem,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.heightFt,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.weightLbs,
            LocalStorageKeys[RoutePath.BodyIdealWeight],
        ]
    },
    [RoutePath.HealthCondition]: {
        header: {
            email: false,
            logo: true,
            progress: {
                percents: 64,
                label: '',
            },
        },
        route: {
            next: (data) => data?.declineUser ? RoutePath.DeclineUser : RoutePath.MedicalCondition,
        },
        events: {
            onLeave: () => 'SemaRxWeightHealthConditionClicked'
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender],
            LocalStorageKeys[RoutePath.LoseWeightReason],
            MultipleLocalStorageKeys.bodyHeightWeightKeys.unitSystem,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.heightFt,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.weightLbs,
            LocalStorageKeys[RoutePath.BodyIdealWeight],
        ]
    },
    [RoutePath.MedicalCondition]: {
        header: {
            email: false,
            logo: true,
            progress: {
                percents: 68,
                label: '',
            },
        },
        route: {
            next: () => RoutePath.MedicationIntake
        },
        events: {
            onLeave: () => 'SemaRxWeightMedicalConditionClicked'
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender],
            LocalStorageKeys[RoutePath.LoseWeightReason],
            MultipleLocalStorageKeys.bodyHeightWeightKeys.unitSystem,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.heightFt,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.weightLbs,
            LocalStorageKeys[RoutePath.BodyIdealWeight],
        ]
    },
    [RoutePath.MedicationIntake]: {
        header: {
            email: false,
            logo: true,
            progress: {
                percents: 72,
                label: '',
            },
        },
        route: {
            next: () => RoutePath.Consent
        },
        events: {
            onLeave: () => 'SemaRxWeightMedicationIntakeClicked'
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender],
            LocalStorageKeys[RoutePath.LoseWeightReason],
            MultipleLocalStorageKeys.bodyHeightWeightKeys.unitSystem,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.heightFt,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.weightLbs,
            LocalStorageKeys[RoutePath.BodyIdealWeight],
        ]
    },
    [RoutePath.Consent]: {
        header: {
            email: false,
            logo: true,
            progress: {
                percents: 76,
                label: '',
            },
        },
        route: {
            next: () => RoutePath.ScientificProof
        },
        events: {
            onLeave: () => 'SemaRxWeightConsentClicked'
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender],
            LocalStorageKeys[RoutePath.LoseWeightReason],
            MultipleLocalStorageKeys.bodyHeightWeightKeys.unitSystem,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.heightFt,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.weightLbs,
            LocalStorageKeys[RoutePath.BodyIdealWeight],
        ]
    },
    [RoutePath.ScientificProof]: {
        scrollableContent: true,
        header: {
            email: false,
            logo: true,
            progress: null,
        },
        route: {
            next: () => RoutePath.ImportantEvent,
        },
        events: {
            onEnter: () => 'SemaRxWeightYoYoScientificProofViewed',
            onLeave: () => 'SemaRxWeightYoYoScientificProofClicked',
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender],
            LocalStorageKeys[RoutePath.LoseWeightReason],
            MultipleLocalStorageKeys.bodyHeightWeightKeys.unitSystem,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.heightFt,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.weightLbs,
            LocalStorageKeys[RoutePath.BodyIdealWeight],
        ]
    },
    [RoutePath.ImportantEvent]: {
        customWrapper: false,
        header: {
            email: false,
            logo: true,
            progress: {
                percents: 77,
                label: '',
            },
        },
        route: {
            next: (data: any) => {
                if (data.key !== 'none') {
                    return RoutePath.ImportantEventDate;
                } else {
                    return RoutePath.AnalyzeResponses;
                }
            }
        },
        events: {
            onLeave: () => 'SemaRxWeightImportantEventClicked',
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender],
            LocalStorageKeys[RoutePath.LoseWeightReason],
            MultipleLocalStorageKeys.bodyHeightWeightKeys.unitSystem,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.heightFt,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.weightLbs,
            LocalStorageKeys[RoutePath.BodyIdealWeight],
        ]
    },
    [RoutePath.ImportantEventDate]: {
        customWrapper: false,
        header: {
            email: false,
            logo: true,
            progress: null,
        },
        route: {
            next: () => RoutePath.AnalyzeResponses,
        },
        events: {},
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender],
            LocalStorageKeys[RoutePath.LoseWeightReason],
            MultipleLocalStorageKeys.bodyHeightWeightKeys.unitSystem,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.heightFt,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.weightLbs,
            LocalStorageKeys[RoutePath.BodyIdealWeight],
        ]
    },
    [RoutePath.AnalyzeResponses]: {
        header: {
            email: false,
            logo: true,
            progress: {
                percents: null,
                label: '',
            },
        },
        route: {
            next: () => RoutePath.Email,
        },
        events: {
            onLeave: () => 'SemaRxWeightAnalyzeResponsesCompleted'
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender],
            LocalStorageKeys[RoutePath.LoseWeightReason],
            MultipleLocalStorageKeys.bodyHeightWeightKeys.unitSystem,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.heightFt,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.weightLbs,
            LocalStorageKeys[RoutePath.BodyIdealWeight],
        ]
    },
    [RoutePath.Email]: {
        header: {
            email: false,
            logo: true,
            progress: {
                percents: 80,
                label: '',
            },
        },
        route: {
            next: (data) => {
                if (data?.isUserExists) {
                    handleRedirectToPwa();
                }

                return RoutePath.PrePaywall;
            },
        },
        events: {
            onLeave: () => 'SemaRxWeightEmailClicked'
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender],
            LocalStorageKeys[RoutePath.LoseWeightReason],
            MultipleLocalStorageKeys.bodyHeightWeightKeys.unitSystem,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.heightFt,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.weightLbs,
            LocalStorageKeys[RoutePath.BodyIdealWeight],
        ]
    },
    [RoutePath.PrePaywall]: {
        customWrapper: true,
        header: {
            email: true,
            logo: false,
            progress: {
                percents: null,
                label: '',
            },
        },
        route: {
            next: () => RoutePath.Medication,
        },
        events: {
            onLeave: () => 'SemaRxWeightPrePaywallClicked'
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender],
            LocalStorageKeys[RoutePath.LoseWeightReason],
            MultipleLocalStorageKeys.bodyHeightWeightKeys.unitSystem,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.heightFt,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.weightLbs,
            LocalStorageKeys[RoutePath.BodyIdealWeight],
            LocalStorageKeys[RoutePath.Email]
        ]
    },
    [RoutePath.Medication]: {
        customWrapper: true,
        header: {
            email: true,
            logo: false,
            progress: {
                percents: null,
                label: '',
            },
        },
        footerAtTheBottom: true,
        route: {
            next: () => RoutePath.Checkout,
        },
        events: {
            onLeave: () => 'SemaRxWeightMedicationClicked'
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender],
            LocalStorageKeys[RoutePath.LoseWeightReason],
            MultipleLocalStorageKeys.bodyHeightWeightKeys.unitSystem,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.heightFt,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.weightLbs,
            LocalStorageKeys[RoutePath.BodyIdealWeight],
            LocalStorageKeys[RoutePath.Email]
        ]
    },
    [RoutePath.Checkout]: {
        customWrapper: true,
        header: {
            email: false,
            logo: true,
            progress: {
                percents: null,
                label: '',
            },
        },
        route: {
            next: () => RoutePath.Congrats
        },
        events: {},
        requiredKeys: [
            LocalStorageKeys[RoutePath.BodyGender],
            LocalStorageKeys[RoutePath.LoseWeightReason],
            MultipleLocalStorageKeys.bodyHeightWeightKeys.unitSystem,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.heightFt,
            MultipleLocalStorageKeys.bodyHeightWeightKeys.weightLbs,
            LocalStorageKeys[RoutePath.BodyIdealWeight],
            LocalStorageKeys[RoutePath.Email]
        ]
    },
    [RoutePath.Congrats]: {
        header: {
            email: true,
            logo: false,
            progress: null
        },
        route: {
            next: () => RoutePath.Welcome,
        },
        events: {
            onEnter: () => 'SemaRxWeightCongratsViewed',
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.Email]
        ]
    },
    [RoutePath.Welcome]: {
        customWrapper: true,
        header: {
            email: true,
            logo: false,
            progress: null
        },
        route: {
            next: () => RoutePath.Welcome,
        },
        events: {
            onLeave: () => 'SemaRxWeightDownloadButtonClicked',
        },
        requiredKeys: [
            LocalStorageKeys[RoutePath.Email]
        ]
    },
    [RoutePath.DeclineUser]: {
        header: {
            email: false,
            logo: true,
            progress: {
                percents: null,
                label: '',
            },
        },
        route: {
            next: () => RoutePath.BodyGender,
        },
        events: {
            onLeave: () => 'SemaRxWeightContinueWithoutMedicationsClicked'
        }
    },
    [RoutePath.ShippingAddressEmbedded]: { // Page for opening in WebView
        customWrapper: true,
        scrollableContent: false,
        header: {
            email: false,
            logo: false,
            progress: null,
        },
        route: {
            next: () => RoutePath.ShippingAddressEmbedded,
        },
        events: {}
    }
};
